import DangerZone from '$components/DangerZone'
import DeleteDoubleConfirmPopUp from '$components/DeleteDoubleConfirmPopUp'
import Modal from '$components/Modal'
import { useModal } from '$components/Modal/hooks/useModal'
import { useUserService } from '$hooks/services'
import { useProfileDataStore } from '$layouts/UserProfileLayout/hooks/useProfileDataStore'
import {
  innerMainContent,
  mainContent
} from '$layouts/UserProfileLayout/styles.css'
import { flexColumn } from '$styles/common.css'
import { Button, ButtonProps, Spinner } from '@genie-fintech/ui/components'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { Ban, History } from 'lucide-react'
import { useCallback } from 'react'
import StatusHistory from '../../../../components/StatusHistory'
import { usePermissions } from '$hooks/actions'
import { PERMISSIONS } from '$app/constants'
import { container, modalContainer } from './styles.css'

const { colors } = themeVars

const buttonVariants: ButtonProps['styleVariants'] = {
  kind: 'neutral',
  type: 'outlined',
  size: 'small'
}

const UserActivationSetting = () => {
  const modalActions = useModal({
    closeOnClickOutside: false,
    isFocusMode: true
  })

  const profile = useProfileDataStore(state => state.profile)

  const { id: userId = '', status, name = '' } = { ...profile }

  const isActive = status === 'active'

  const { updateUserStatusAsync, updatingUserStatus } = useUserService()

  const { checkPermissions } = usePermissions()
  const CAN_BAN = checkPermissions([PERMISSIONS.USER_BAN])

  const onTerminateUser = useCallback(() => {
    updateUserStatusAsync({
      userId,
      isAdmin: false,
      status: 'inactive'
    })
  }, [updateUserStatusAsync, userId])

  const onActivateUser = useCallback(() => {
    updateUserStatusAsync({
      userId,
      isAdmin: false,
      status: 'active'
    })
  }, [updateUserStatusAsync, userId])

  const ViewHistoryButton = () => (
    <Button styleVariants={buttonVariants} onClick={modalActions.openModal}>
      View History
      <History size={16} />
    </Button>
  )

  return (
    <>
      <article className={mainContent}>
        <article className={innerMainContent}>
          {isActive && (
            <DangerZone type="user" rightNav={<ViewHistoryButton />}>
              <DeleteDoubleConfirmPopUp
                item={{ name, type: 'user' }}
                onExecute={onTerminateUser}
                loading={updatingUserStatus}
                disabled={!CAN_BAN}
              >
                <Button
                  styleVariants={{
                    kind: 'danger',
                    type: 'text',
                    size: 'small'
                  }}
                  disabled={!CAN_BAN}
                >
                  <Ban size={20} />
                  Terminate User
                </Button>
              </DeleteDoubleConfirmPopUp>
            </DangerZone>
          )}

          {!isActive && (
            <article className={container}>
              <article className={flexColumn} style={{ gap: 4, flex: 1 }}>
                <p className={title.two}>User Reactivation</p>
                <p
                  className={footnote.one}
                  style={{ color: colors.neutral[60] }}
                >
                  You can reactivate this user at any time.
                  <br />
                  Once reactivated, the user will regain access to their account
                  and services.
                </p>
              </article>

              <span style={{ flex: 1 }} />

              <ViewHistoryButton />

              <Button
                styleVariants={{ kind: 'primary', size: 'small' }}
                disabled={updatingUserStatus}
                onClick={onActivateUser}
              >
                {updatingUserStatus && <Spinner />}
                Reactivate User
              </Button>
            </article>
          )}
        </article>
      </article>

      <Modal {...modalActions} contentProps={{ className: modalContainer }}>
        <article>
          <Button
            styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
            onClick={modalActions.closeModal}
          >
            Back
          </Button>
        </article>

        <p className={title.two}>USER STATUS HISTORY</p>

        <StatusHistory userId={userId} />
      </Modal>
    </>
  )
}

export default UserActivationSetting
